/**
 * Helper functions to detect features we can use to uniquely identify the device
 *
 * @author    Mariel Bartolome <mbartolome@wayfair.com>
 * @author    Seraphina Orsini <sorsini@wayfair.com>
 * @copyright 2020 Wayfair, LLC - All rights reserved
 */

// Private browsing is not going to change in a single session
let cachedIsPrivateBrowsing = null;

// this can be spoofed, but is the closest to reliable we have
const getUserAgent = () =>
  typeof navigator !== 'undefined' ? navigator.userAgent : null;

/**
 * Checks sessionStorage to see if the device in Safari private browsing mode
 */

const _isPrivateBrowsingSafari = () =>
  new Promise(resolve => {
    let storage;
    try {
      storage = window.sessionStorage;
      if (!storage) {
        resolve(false);
      }
      storage.setItem('a', 'z');
      storage.removeItem('a');
    } catch (e) {
      if (
        e.code === DOMException.QUOTA_EXCEEDED_ERR &&
        storage &&
        storage.length === 0
      ) {
        resolve(true);
      }
    }
    resolve(false);
  });

/**
 * Checks RequestsFileSystem to see if the device is in Chrome incognito mode. Return false if RequestsFileSystem is not
 * a feature that we can check against
 */

const _isPrivateBrowsingChrome = () =>
  new Promise(resolve => {
    try {
      const fs = window.RequestFileSystem || window.webkitRequestFileSystem;
      if (!fs) {
        resolve(false);
        return;
      } // File system request: fs(type of storage, requested bytes, on success callback, on error callback)
      fs(window.TEMPORARY, 1, () => resolve(false), () => resolve(true));
    } catch (e) {
      resolve(false);
    }
  });

/**
 * Checks indexedDb to see if the device is in Firefox private browsing mode. Return false if indexedDB is not
 * a feature that we can check against
 */

const _isPrivateBrowsingFirefox = () =>
  new Promise(resolve => {
    try {
      const indexedDB = window.indexedDB;
      if (!indexedDB) {
        resolve(false);
        return;
      }
      const dbTest = indexedDB.open('a');
      dbTest.onsuccess = function() {
        resolve(false);
      };
      dbTest.onerror = function(e) {
        // prevent error event if possible
        if (e && (typeof e.cancelable !== 'boolean' || e.cancelable)) {
          e.preventDefault();
        }
        resolve(true); // Return true or else this throws an uncatchable "InvalidStateError"
        return true;
      };
    } catch (e) {
      resolve(false);
    }
  });

/**
 * Checks to see if the device is in IE privacy mode by checking browser feature existence
 */

const _isPrivateBrowsingIe = () =>
  new Promise(resolve => {
    try {
      return resolve(
        !!(!window.indexedDB && (window.PointerEvent || window.MSPointerEvent))
      );
    } catch (e) {
      return resolve(false);
    }
  });

const isPrivateBrowsing = () => {
  if (cachedIsPrivateBrowsing != null) {
    return cachedIsPrivateBrowsing;
  }

  const browserName = getUserAgent();
  if (!browserName) {
    cachedIsPrivateBrowsing = Promise.resolve(false);
  } else {
    cachedIsPrivateBrowsing = (() => {
      if (browserName.indexOf('Safari') !== -1) {
        return _isPrivateBrowsingSafari();
      } else if (browserName.indexOf('Chrome') !== -1) {
        return _isPrivateBrowsingChrome();
      } else if (browserName.indexOf('Firefox') !== -1) {
        return _isPrivateBrowsingFirefox();
      } else if (browserName.indexOf('Explorer') !== -1) {
        return _isPrivateBrowsingIe();
      }
      return Promise.resolve(false);
    })();
  }
  return cachedIsPrivateBrowsing;
};

export default isPrivateBrowsing;
